import type { UserPermissionDatabaseEntryDto, UserPermissionsByDatabaseDto } from '@/libs/api-user-mgt/generated/model';
import { userManagementAxiosInstance } from '@/libs/api-user-mgt';
import { getApiUserManagementV1Features } from '@/libs/api-user-mgt/generated/features/features';
import { getApiUserManagementV1RolesRoleId } from '@/libs/api-user-mgt/generated/permission-roles/permission-roles';
import {
  getApiUserManagementV1UsersUsername,
  getApiUserManagementV1UsersUsernamePermissions
} from '@/libs/api-user-mgt/generated/users/users';

import type { Feature } from '@/userManagement/domain/entities/Feature';
import type { UserPermissions, Permission } from '@/userManagement/domain/entities/Permission';
import type { TenantDatabaseEntry, User } from '@/userManagement/domain/entities/User';
import { computePermissionDeviations } from '@/userManagement/domain/services/permissionDeviationUtils';
import { mapPermissionFlagsWireToPermissions } from '@/userManagement/domain/services/permissionWireMapper';
import { mapCrossUserPayloadToWireDto } from '@/userManagement/application/mappers/crossUserListItemMapper';
import { featureCatalogMap } from '@/userManagement/application/mappers/featureCatalogMapper';
import {
  flattenUserPermissionsDetailToFlagsWire,
  type UserPermissionsDetailWire
} from '@/userManagement/application/mappers/userPermissionsDetailMapper';
import { mapUserDtoToEntity } from '@/userManagement/application/mappers/userMapper';
import { pickPermissionFlagsFromUserPermissionsByDatabaseDto } from '@/userManagement/application/mappers/userPermissionsByDatabaseMapper';
import type { UserWireDto } from '@/userManagement/application/types/UserWireDto';

import { normalizeFeaturesResponse } from './featuresWire';
import { normalizeRolePermissionTemplate } from './rolePermissionsWire';

const ROLE_GET_RESPONSE_CACHE_TTL_MS = 15 * 60 * 1000;
const roleGetResponseCache = new Map<number, { expiresAt: number; data: unknown }>();

async function getRoleResponseDataCached(roleId: number): Promise<unknown> {
  const now = Date.now();
  const hit = roleGetResponseCache.get(roleId);
  if (hit != null && hit.expiresAt > now) {
    return hit.data;
  }
  const roleRes = await getApiUserManagementV1RolesRoleId(roleId);
  roleGetResponseCache.set(roleId, { expiresAt: now + ROLE_GET_RESPONSE_CACHE_TTL_MS, data: roleRes.data });
  return roleRes.data;
}

function firstArrayEntry(record: Record<string, unknown>): UserPermissionDatabaseEntryDto[] | undefined {
  const items = record.items ?? record.Items;
  if (Array.isArray(items)) {
    return items as UserPermissionDatabaseEntryDto[];
  }
  for (const key of ['value', 'result', 'data'] as const) {
    const v = record[key];
    if (Array.isArray(v)) {
      return v as UserPermissionDatabaseEntryDto[];
    }
  }
  return undefined;
}

/**
 * UM may return `{ items: [...] }` (OpenAPI), PascalCase `Items`, a raw JSON array, or a small envelope
 * (`value` / `result` / `data` holding the array). Normalize so downstream code always reads `dto.items`.
 */
export function normalizeUserPermissionsByDatabaseDto(raw: unknown): UserPermissionsByDatabaseDto | undefined {
  if (raw === undefined || raw === null) return undefined;
  if (Array.isArray(raw)) {
    return { items: raw as UserPermissionDatabaseEntryDto[] };
  }
  if (typeof raw === 'object') {
    const record = raw as Record<string, unknown>;
    const fromRecord = firstArrayEntry(record);
    if (fromRecord) {
      return { items: fromRecord };
    }
    const permissionsOnly = record.permissions ?? record.Permissions;
    if (Array.isArray(permissionsOnly)) {
      return { items: permissionsOnly as UserPermissionDatabaseEntryDto[] };
    }
  }
  return undefined;
}

/** Cached result of user + permissions DTO + features + role template (no per-database slice). */
export interface UserPermissionsCachedPayload {
  crossUserId: string;
  permissionsDto: UserPermissionsByDatabaseDto | undefined;
  user: User;
  features: Feature[];
  roleTemplate: Permission[] | null;
}

export function tenantDatabaseNamesFromPermissionsDto(dto: UserPermissionsByDatabaseDto | undefined): string[] {
  return tenantDatabaseEntriesFromPermissionsDto(dto).map((e) => e.tenantDatabaseName);
}

export function tenantDatabaseEntriesFromPermissionsDto(dto: UserPermissionsByDatabaseDto | undefined): TenantDatabaseEntry[] {
  const items = dto?.items ?? [];
  const seen = new Set<string>();
  const out: TenantDatabaseEntry[] = [];
  for (const item of items) {
    const tenantDatabaseName = (item.tenantDatabaseName ?? '').trim();
    if (!tenantDatabaseName) continue;
    const key = tenantDatabaseName.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    const aliasRaw = item.databaseAlias;
    const databaseAlias = typeof aliasRaw === 'string' && aliasRaw.trim() !== '' ? aliasRaw.trim() : null;
    out.push({ tenantDatabaseName, databaseAlias, ...(item.databaseId ? { databaseId: item.databaseId } : {}) });
  }
  return out.sort((a, b) =>
    (a.databaseAlias ?? a.tenantDatabaseName).localeCompare(b.databaseAlias ?? b.tenantDatabaseName, undefined, {
      sensitivity: 'base'
    })
  );
}

/** Loads role permission templates for distinct role ids (one GET per id). Used by directory-bundle snapshot compare. */
export async function fetchRoleTemplatesForRoleIds(
  roleIds: readonly number[],
  featureById: ReturnType<typeof featureCatalogMap>
): Promise<Map<number, Permission[] | null>> {
  const uniq = [...new Set(roleIds.filter((id) => typeof id === 'number' && Number.isInteger(id) && id > 0))];
  const out = new Map<number, Permission[] | null>();
  await Promise.all(
    uniq.map(async (roleId) => {
      const t = await loadRoleTemplatePermissions(roleId, featureById, {
        crossUserId: `role:${roleId}`,
        source: 'fetchRoleTemplatesForRoleIds'
      });
      out.set(roleId, t);
    })
  );
  return out;
}

async function loadRoleTemplatePermissions(
  permissionRoleId: number | null | undefined,
  featureById: ReturnType<typeof featureCatalogMap>,
  log: { crossUserId: string; source: string }
): Promise<Permission[] | null> {
  const id = permissionRoleId != null && permissionRoleId > 0 ? permissionRoleId : null;
  if (id == null) return null;

  const rolePayload = (await getRoleResponseDataCached(id)) as
    | {
        permissions?: unknown;
        permissionTemplate?: unknown;
      }
    | null
    | undefined;

  if (rolePayload == null) {
    console.warn(`[userManagement] ${log.source}: role response data missing`, {
      crossUserId: log.crossUserId,
      roleId: id
    });
    return null;
  }

  const normalizedTemplate = normalizeRolePermissionTemplate(rolePayload);
  if (normalizedTemplate !== undefined) {
    return mapPermissionFlagsWireToPermissions(normalizedTemplate, featureById);
  }

  console.warn(`[userManagement] ${log.source}: role response did not contain a recognizable permission template; skipping`, {
    crossUserId: log.crossUserId,
    roleId: id
  });
  return null;
}

export function finalizeUserPermissionsFromCached(payload: UserPermissionsCachedPayload, database?: string): UserPermissions {
  const permissionsRows = pickPermissionFlagsFromUserPermissionsByDatabaseDto(payload.permissionsDto, database);
  const featureById = featureCatalogMap(payload.features);
  const permissions: Permission[] = mapPermissionFlagsWireToPermissions(permissionsRows, featureById);

  const deviations: UserPermissions['deviations'] = [];
  if (payload.roleTemplate && payload.roleTemplate.length > 0) {
    deviations.push(...computePermissionDeviations(permissions, payload.roleTemplate));
  }

  return {
    userId: payload.crossUserId,
    permissions,
    roleTemplate: payload.roleTemplate,
    deviations
  };
}

/**
 * One network round-trip for permissions DTO + user + features, then role template when applicable.
 * Per-database rows are applied later via {@link finalizeUserPermissionsFromCached} (same GET returns all `items[]`).
 */
export async function fetchUserPermissionsCachedPayload(username: string, crossUserId: string): Promise<UserPermissionsCachedPayload> {
  const userKey = username.trim();
  if (userKey === '') {
    throw new Error('fetchUserPermissionsCachedPayload: username is required (GET /users/{username}/permissions).');
  }

  const [permissionsRes, userRes, featuresRes] = await Promise.all([
    getApiUserManagementV1UsersUsernamePermissions(userKey),
    getApiUserManagementV1UsersUsername(userKey),
    getApiUserManagementV1Features()
  ]);

  const wire = mapCrossUserPayloadToWireDto(userRes.data);
  if (!wire) {
    console.warn('[userManagement] fetchUserPermissionsCachedPayload: user response could not be mapped; using minimal entity', {
      crossUserId,
      username: userKey
    });
  }
  const user = wire != null ? mapUserDtoToEntity(wire) : mapUserDtoToEntity({ ...EMPTY_USER_WIRE, id: crossUserId, username: userKey });

  const features: Feature[] = normalizeFeaturesResponse(featuresRes.data);

  const featureById = featureCatalogMap(features);
  const roleTemplate = await loadRoleTemplatePermissions(user.permissionRoleId, featureById, {
    crossUserId,
    source: 'fetchUserPermissionsCachedPayload'
  });

  const permissionsDto = normalizeUserPermissionsByDatabaseDto(permissionsRes.data);
  if (import.meta.env.DEV && permissionsRes.data != null && permissionsDto === undefined) {
    const raw = permissionsRes.data;
    const hint = typeof raw === 'object' && raw !== null && !Array.isArray(raw) ? Object.keys(raw as object).join(',') : typeof raw;
    console.warn('[userManagement] Unexpected GET .../permissions body shape; expected array or { items }', {
      hint
    });
  }

  return {
    crossUserId,
    permissionsDto,
    user,
    features,
    roleTemplate
  };
}

/**
 * GET /users/{crossUserId}/permissions?dbName=… — effective permissions for one tenant database
 * (categories-based payload), with role template + deviations aligned to {@link fetchUserPermissionsCachedPayload}.
 */
export async function fetchUserPermissionsForDatabase(
  crossUserId: string,
  databaseName: string,
  _username: string
): Promise<UserPermissions> {
  const db = databaseName.trim();
  if (db === '') {
    throw new Error('fetchUserPermissionsForDatabase: databaseName is required.');
  }

  const [permRes, featuresRes] = await Promise.all([
    userManagementAxiosInstance<unknown>({
      url: `/api/user-management/v1/users/${crossUserId}/permissions`,
      method: 'GET',
      params: { dbName: db }
    }),
    getApiUserManagementV1Features()
  ]);

  const detail = permRes.data as UserPermissionsDetailWire | undefined;
  const features: Feature[] = normalizeFeaturesResponse(featuresRes.data);
  const featureById = featureCatalogMap(features);
  const rows = flattenUserPermissionsDetailToFlagsWire(detail);
  const permissions = mapPermissionFlagsWireToPermissions(rows, featureById);

  const roleIdFromDetail = detail?.permissionRole?.id;
  const roleTemplate = await loadRoleTemplatePermissions(roleIdFromDetail, featureById, {
    crossUserId,
    source: 'fetchUserPermissionsForDatabase'
  });

  const deviations: UserPermissions['deviations'] = [];
  if (roleTemplate && roleTemplate.length > 0) {
    deviations.push(...computePermissionDeviations(permissions, roleTemplate));
  }

  return {
    userId: crossUserId,
    permissions,
    roleTemplate,
    deviations
  };
}

/** Loads effective permissions, optional role template, and deviations for one user. */
export async function fetchUserPermissions(username: string, crossUserId: string, database?: string): Promise<UserPermissions> {
  const payload = await fetchUserPermissionsCachedPayload(username, crossUserId);
  return finalizeUserPermissionsFromCached(payload, database);
}

const EMPTY_USER_WIRE: UserWireDto = {
  id: '',
  username: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: null,
  permissionRoleId: null,
  permissionRoleName: null,
  isLocked: false,
  isSuspended: false,
  isActive: true,
  forceNewPassword: false,
  failedLoginAttempts: 0,
  lastLoginOn: null,
  assignedDatabases: [],
  utcUpdatedOn: new Date().toISOString()
};
